import { type Customer } from '@hungryroot/customer';
import { useCustomerDietaryRestrictions } from '@hungryroot/dietary-restrictions';
import { FoodQualities } from '@hungryroot/icons';
import {
  FoodProfileDisclosure,
  TagPreference,
} from '@hungryroot/page-food-profile';
import { Switch } from '@hungryroot/pho';
import { Paragraph } from '@hungryroot/ramen';
import { type PreferenceOption } from '@hungryroot/rest/api/v2/preference_options/get/get_preference_options';

import { useFoodProfilePage } from '../../utils/useFoodProfilePage';
import styles from './FoodQualitiesSection.module.css';

const grassFedMeatTagId = 815;
const wildCaughtSeafoodTagId = 806;

const dietaryExlcusionsMap: Record<string, number[]> = {
  is_vegan: [grassFedMeatTagId, wildCaughtSeafoodTagId],
  is_vegetarian: [grassFedMeatTagId, wildCaughtSeafoodTagId],
  is_pescatarian: [grassFedMeatTagId],
};

const getSwitchIcon = (tag: PreferenceOption) => {
  const imageUrl = tag.image_url || tag.thumbnail_url;
  if (!imageUrl) return null;
  return <img alt={tag.name} src={imageUrl} className={styles.icon} />;
};

interface Props {
  testid?: string;
  customerId: Customer['id'];
}

export function FoodQualitiesSection(props: Props) {
  const { testid, customerId } = props;

  const { data: dietaryRestrictionsData } = useCustomerDietaryRestrictions();

  const dietaryBasedTagExclusion =
    dietaryRestrictionsData?.dietary_restrictions.flatMap(
      ({ field_name }) => dietaryExlcusionsMap[field_name]
    );

  const { preferencesAndTags, subtitle, onSwitchSelect } = useFoodProfilePage({
    customerId,
    page: 'preference_options.food_quality',
    tagExclusions: dietaryBasedTagExclusion,
  });

  return (
    <section data-testid={testid} className={styles.container}>
      <FoodProfileDisclosure
        testid={`${testid}-disclosure`}
        title="Food qualities"
        subtitle={subtitle || 'Set your preferences.'}
        icon={<FoodQualities />}
        deepLink="food-qualities"
      >
        <Paragraph className={styles.text}>
          Your preferences help us suggest more recipes to fit how you eat.
        </Paragraph>
        <div className={styles['switch-layout']}>
          {preferencesAndTags?.map(([preference, tag]) => {
            return (
              <Switch
                key={tag.tag_id}
                testid={`${testid}-${tag.tag_id}`}
                label={tag.name}
                enabled={preference === TagPreference.Selected}
                onChange={() => {
                  onSwitchSelect(tag, !preference);
                  if (tag.associated_tag_ids?.length) {
                    tag.associated_tag_ids.forEach((tagId) => {
                      onSwitchSelect({ tag_id: tagId }, !preference);
                    });
                  }
                }}
                icon={getSwitchIcon(tag)}
              />
            );
          })}
        </div>
      </FoodProfileDisclosure>
    </section>
  );
}
